import {
  parseISO,
  format,
  getDaysInMonth,
  getMonth,
  intervalToDuration,
} from 'date-fns';
import sanitizeHtml from 'sanitize-html';

export const capitalize = (text) => {
  const val = text.split('_').join(' ');
  return val.charAt(0).toUpperCase() + val.slice(1);
};

export const formatDateTime = (value) => {
  // format date to date and time
  // e.g. 2 september 2019 - 14:44
  if (!value) return '';
  // if value has'n been parsed, then parse to Date
  if (!(value instanceof Date)) {
    value = parseISO(value);
  }
  return format(value, 'dd MMM yyyy HH:mm');
};

export const formatDate = (value) => {
  if (!value) return '';
  // if value has'n been parsed, then parse to Date
  if (!(value instanceof Date)) {
    value = parseISO(value);
  }
  return format(value, 'dd MMM yyyy');
};

export const titleCase = (value) => {
  // format title key
  // e.g. 'title_case' to 'Title Case'
  if (!value) return '';
  const splitStr = value.toLowerCase().split('_');
  const capitalizedStr = splitStr.map((str) => capitalize(str));
  return capitalizedStr.join(' ');
};

export const isEmptyObject = (obj) => {
  if (typeof obj !== 'object') return false;
  return Object.keys(obj).length === 0;
};

export const highlightString = (str, keyword) => {
  if (!str) return null;
  const sanitizedInput = sanitizeHtml(str);
  const rex = new RegExp(keyword, 'gi');
  return sanitizedInput.replace(rex, '<span class="text-danger">$&</span>');
};
export const parsePayloadKeyTooltip = (payloadKey) => {
  switch (payloadKey) {
    case 'soil_wt1':
      return { name: 'Water Tension 1', unit: 'cBar', icon: 'wi-barometer' };
    case 'soil_wt2':
      return { name: 'Water Tension 2', unit: 'cBar', icon: 'wi-barometer' };
    case 'soil_vmc':
      return { name: 'Volumetric Water Content', unit: '%', icon: 'wi-raindrop' };
    default:
      return { name: payloadKey, unit: '' };
  }
};

export const parsePayloadKey = (payloadKey) => {
  const fallbackValue = {
    // generate default values
    name: (function () {
      if (Array.isArray(payloadKey)) {
        payloadKey = payloadKey[0];
      }
      if (payloadKey == null) {
        return payloadKey;
      }
      return capitalize(payloadKey);
    }()),
    unit: '',
    icon: 'fa-star',
  };

  // payload key dictionary
  if (!payloadKey) return '';
  // handling when payload key is array
  if (Array.isArray(payloadKey)) {
    payloadKey = payloadKey[0];
  }
  const sanitizedValue = payloadKey.toString().toLowerCase();

  switch (sanitizedValue) {
    case 'soil_dp':
      return { name: 'Soil Dielectric Permittivity', unit: '', icon: 'wi-flood' };
    case 'soil_wt':
      return { name: 'Soil Water Tension', unit: 'cbar', icon: 'wi-barometer' };
    case 'soil_wt1':
      return { name: 'Soil Water Tension 1', unit: 'cbar', icon: 'wi-barometer' };
    case 'soil_wt2':
      return { name: 'Soil Water Tension 2', unit: 'cbar', icon: 'wi-barometer' };
    case 'soil_vmc':
      return { name: 'Soil Volumetric Water Content', unit: '%', icon: 'wi-raindrop' };
    case 'soil_ec':
      return { name: 'Soil Electrical Conductivity', unit: 'dS/m', icon: 'wi-earthquake' };
    case 'soil_temp':
      return { name: 'Soil Temperature', unit: '°C', icon: 'wi-thermometer' };
    case 'solar_uv':
      return { name: 'Ultraviolet Sensor', unit: 'UVR', icon: 'wi-day-sunny' };
    case 'solar_par':
      return { name: 'Photosynthetically Active Radiation', unit: 'µmol m-2 s-1', icon: 'wi-hot' };
    case 'rtd_temp':
      return { name: 'RTD Temperature', unit: '°C', icon: 'wi-thermometer' };
    case 'rssi':
      return { name: 'RSSI', unit: 'dB' };
    case 'snr':
      return { name: 'SNR', unit: '' };
    case 'water_flow':
      return { name: 'Water Flow', unit: 'Liter', icon: 'wi-raindrops' };
    case 'water_vol':
      return { name: 'Water Volume', unit: 'Liter', icon: 'wi-raindrop' };
    case 'water_temp':
      return { name: 'Water Temperature', unit: '°C', icon: 'wi-thermometer' };
    case 'pH':
      return { name: 'PH', unit: '', icon: 'wi-thermometer-internal' };
    case 'redox':
      return { name: 'Redox', unit: '', icon: 'fa-flask' };
    case 'batt':
      return { name: 'Battery', unit: 'Volt', icon: 'fa-battery-full' };
    case 'air_hum':
      return { name: 'Air Humidity', unit: '%', icon: 'wi-humidity' };
    case 'air_temp':
      return { name: 'Air Temp', unit: '°C', icon: 'wi-thermometer' };
    case 'avg_wind_speed':
      return { name: 'Avg Wind Speed', unit: 'm/s', icon: 'wi-strong-wind' };
    case 'solar_rad':
      return { name: 'Solar Radiation', unit: 'W/m2', icon: 'wi-hot' };
    case 'sun_hour':
      return { name: 'Sun Hour', unit: 'Hours', icon: 'wi-day-sunny' };
    case 'supply_volt':
      return { name: 'Supply Volt', unit: 'Volt', icon: 'fa-bolt' };
    case 'read_retry':
      return { name: 'Read Retry', unit: '', icon: 'fa-repeat' };
    case 'wind_dir':
      return { name: 'Wind Direction', unit: '°', icon: 'wi-wind wi-towards-nne' };
    case 'wind_speed':
      return { name: 'Wind Speed', unit: 'm/s', icon: 'wi-strong-wind' };
    case 'frequency':
      return { name: 'Frequency', unit: 'MHz', icon: 'fa-bullseye' };
    case 'cvolt':
      return { name: 'Cvolt', unit: 'Volt', icon: 'fa-bolt' };
    case 'precipintensity':
      return { name: 'Precipitation Intensity', unit: 'mm', icon: 'wi-sleet' };
    case 'preciptotal':
      return { name: 'Precipitation Total', unit: 'mm', icon: 'wi-sleet' };
    case 'sensor_status':
      return { name: 'Sensor Status', unit: '', icon: 'fa-cog' };
    case 'charge':
      return { name: 'Charge', unit: 'mA', icon: 'fa-plug' };
    case 'valve':
      return { name: 'Valve', unit: '', icon: 'fa-shower' };
    case 'sludge_level':
      return { name: 'Sludge Level', unit: 'mg/l', icon: 'wi-thermometer-internal' };
    case 'temp':
      return { name: 'Temperature', unit: '°C', icon: 'wi-thermometer' };
    case 'water_level':
      return { name: 'Water Level', unit: 'm', icon: 'wi-flood' };
    case 'wh':
    case 'kwh':
      return { name: 'kWh', unit: 'kWh', icon: 'fa-superpowers' };
    case 'varh':
      return { name: 'kVARh', unit: '', icon: 'fa-bolt' };
    case 'thd_va':
    case 'thd_vb':
    case 'thd_vc':
      return { name: fallbackValue.name, unit: '%', icon: 'fa-percent' };
    case 'v_a':
    case 'v_ab':
    case 'v_b':
    case 'v_bc':
    case 'v_c':
    case 'v_ca':
      return { name: `Voltage ${fallbackValue.name}`, unit: 'V', icon: 'fa-bolt' };
    case 'i_a':
    case 'i_b':
    case 'i_c':
      return { name: `Current ${fallbackValue.name}`, unit: 'A', icon: 'fa-bolt' };
    case 'kwa':
    case 'kwb':
    case 'kwc':
    case 'kwt':
      return { name: `Power ${fallbackValue.name}`, unit: 'kW', icon: 'fa-bolt' };
    case 'pf_a':
    case 'pf_b':
    case 'pf_c':
      return { name: `Power Factor ${fallbackValue.name}`, unit: '', icon: 'fa-bolt' };
    case 'evaporation':
      return { name: 'Evaporation', unit: 'milimeter (mm)', icon: 'fa-superpowers' };
    case 'water_need':
      return { name: 'Kebutuhan Air', unit: 'm3/fase/bulan/petak', icon: 'wi-raindrop' };
    case 'water_out_swath':
      return { name: 'Total Aktualisasi Outbound', unit: 'm3/bulan', icon: 'wi-raindrop' };
    default:
      return {
        // generate default values
        name: capitalize(payloadKey),
        unit: '',
        icon: 'fa-star',
      };
  }
};

export const getPayloadIcon = (payloadKey) => {
  switch (payloadKey) {
    case 'avgCorrectedWindDirection':
      return 'AvgCorrectedWindDirection';
    case 'avgWindSpeed':
      return 'AvgWindSpeed';
    case 'batt':
      return 'Battery';
    case 'batteryVoltage':
      return 'BatteryVoltage';
    case 'battweather':
      return 'Battweather';
    case 'charge':
      return 'Charge';
    case 'counter':
      return 'Counter';
    case 'currentA':
      return 'CurrentA';
    case 'currentB':
      return 'CurrentB';
    case 'currentC':
      return 'CurrentC';
    case 'cvolt':
      return 'CVolt';
    case 'datetime':
      return 'Datetime';
    case 'evaporation':
      return 'Evaporation';
    case 'energytotal':
      return 'EnergyTotal';
    case 'frequency':
      return 'Frequency';
    case 'gpsAvgCorrectedSpeed':
      return 'GpsAvgCorrectedSpeed';
    case 'icharge':
      return 'ICharge';
    case 'kwh':
      return 'kWh';
    case 'minutes':
      return 'Minutes';
    case 'pfA':
      return 'PfA';
    case 'pfB':
      return 'PfB';
    case 'pfC':
      return 'PfC';
    case 'power':
      return 'Power';
    case 'precipIntensity':
      return 'Precipitation Intensity';
    case 'precipTotal':
      return 'Precipitation Total';
    case 'relative_humidity':
      return 'RelativeHumidity';
    case 'rssi':
      return 'RSSI';
    case 'snr':
      return 'SNR';
    case 'solar_radiation':
      return 'SolarRadiation';
    case 'soil_wt1':
    case 'soil_wt2':
      return 'SoilWaterTension';
    case 'status':
      return 'Status';
    case 'sunshine_hours':
      return 'SunshineHours';
    case 'supply_voltage':
      return 'SupplyVoltage';
    case 'temperature':
      return 'Temperature';
    case 'thdA':
      return 'ThdA';
    case 'thdB':
      return 'ThdB';
    case 'thdC':
      return 'ThdC';
    case 'valve':
      return 'Valve';
    case 'viset':
      return 'VISET';
    case 'voltlmz':
      return 'Voltlmz';
    case 'vsolar':
      return 'VSolar';
    case 'water_vol':
      return 'WaterVolume';
    case 'water_out_swath':
      return 'Water';
    case 'water_need':
      return 'Water';
    case 'wind_sensor_status':
      return 'WindSensorStatus';
    case 'wind_speed':
      return 'WindSpeed';
    default:
      return 'Default';
  }
};

export const parsePayloadsDateRange = (arrUplinks) => arrUplinks
  // reverse payload order
  .map((val) => val.payloads.reverse())
  // convert nested array to one-level array
  .reduce((a, b) => a.concat(b), []);

export const getMonthPeriod = () => {
  const date = new Date();
  return {
    daysInMonth: getDaysInMonth(date),
    currentMonthString: format(date, 'MMMM yyyy'),
    currentMonthNumber: getMonth(date),
  };
};

export const getPlantAge = (startDate) => {
  if (!startDate) return '';
  const { years, months, days } = intervalToDuration({ start: new Date(startDate), end: new Date() });
  return { years, months, days };
};

export const plantCategories = {
  pc: 'Plant Cane (PC)',
  rc1: 'Ratoon Cane 1 (RC1)',
  rc2: 'Ratoon Cane 2 (RC2)',
  rc3: 'Ratoon Cane 3 (RC3)',
  rc4: 'Ratoon Cane 4 (RC4)',
  rc5: 'Ratoon Cane 5 (RC5)',
  rc6: 'Ratoon Cane 6 (RC6)',
};

export const soilTypes = {
  clay: 'Liat',
  peaty: 'Beremah',
  rocky: 'Berbatu',
  other: 'Lainnya',
};

export const getSoilDefaultThreshold = (soilType) => {
  switch (soilType) {
    case 'clay':
      return {
        name: 'Liat',
        threshold_good: 35,
        threshold_poor: 30,
        threshold_critical: 28,
      };
    case 'peaty':
      return {
        name: 'Beremah',
        threshold_good: 35,
        threshold_poor: 30,
        threshold_critical: 28,
      };
    case 'rocky':
      return {
        name: 'Berbatu',
        threshold_good: 35,
        threshold_poor: 30,
        threshold_critical: 28,
      };
    default:
      return {
        threshold_good: 35,
        threshold_poor: 30,
        threshold_critical: 28,
      };
  }
};

export const getPlantPhase = (ageInMonth) => {
  if (ageInMonth >= 0 && ageInMonth <= 2) {
    return {
      key: 'germination',
      name: 'Germination',
    };
  }
  if (ageInMonth >= 3 && ageInMonth <= 5) {
    return {
      key: 'tillering',
      name: 'Tillering',
    };
  }
  if (ageInMonth >= 6 && ageInMonth <= 10) {
    return {
      key: 'growth',
      name: 'Growth',
    };
  }
  if (ageInMonth >= 11 && ageInMonth <= 43) {
    return {
      key: 'rippening',
      name: 'Rippening',
    };
  }
  return null;
};

export const handleInput = (event) => (event.key === '-' ? event.preventDefault() : null);

export const formatNumber = (value) => {
  // format number to thousand and decimal
  // e.g. 1.234  10,99
  if (!(value instanceof Float32Array)) {
    value = parseFloat(value);
  }
  if (value.toString() === 'NaN') {
    return '-';
  }
  // if (value < 100) {
  //   return value.toLocaleString('end-END', { minimumFractionDigits: 2 });
  // }

  return value.toLocaleString('en-EN', { maximumFractionDigits: 2 });
};

export const convertToKwh = (value) => {
  if (Number(value)) {
    const formatedValue = value / 1000;
    return Number(formatedValue);
  }
  return 0;
};

export const getBlockConditionDetail = (block) => {
  const { years, months } = getPlantAge(block.last_period?.start_date);
  if (years * 12 + months >= 11) {
    return {
      key: 'special',
      color: '#423805',
      label: 'KHUSUS',
    };
  }
  const blockDevice = block.devices.find((device) => device.type === 'sensor_soil') || {};
  const observablePayload = blockDevice?.latest_payload?.soil_vmc;
  const defaultThreshold = getSoilDefaultThreshold(block.custom_attributes?.soil_type);
  // Threshold Priority : Block custom > Block Soil Type Threshold > Default Threshold
  const threshold = {
    threshold_good: block.custom_attributes?.threshold_good ?? defaultThreshold.threshold_good,
    threshold_poor: block.custom_attributes?.threshold_poor ?? defaultThreshold.threshold_poor,
    threshold_critical: block.custom_attributes?.threshold_critical ?? defaultThreshold.threshold_critical,
  };

  if (!blockDevice?.latest_payload) {
    return {
      key: '-',
      color: 'light gray',
      label: 'TIDAK ADA DATA',
    };
  }
  if (observablePayload >= threshold.threshold_good) {
    return {
      key: 'good',
      color: 'green',
      label: 'BAIK',
    };
  }
  if (observablePayload >= threshold.threshold_poor) {
    return {
      key: 'poor',
      color: 'yellow',
      label: 'KURANG BAIK',
    };
  }
  if (observablePayload >= threshold.threshold_critical) {
    return {
      key: 'critical',
      color: 'orange',
      label: 'KRITIS',
    };
  }
  return {
    key: 'danger',
    color: 'red',
    label: 'SANGAT KRITIS',
  };
};

export const conditionThresholdShc = [
  {
    key: 'prime',
    minValue: 3,
    color: '#008000',
    text_color: 'white',
    label: 'OPTIMAL',
  },
  {
    key: 'poor',
    minValue: 2,
    color: '#ED3030',
    text_color: 'black',
    label: 'KURANG OPTIMAL',
  },
  {
    key: 'default',
    minValue: 0,
    color: '#FFFFFF',
    text_color: 'black',
    label: 'DEFAULT',
  },
];

export const getStationConditionDetail = (inbound, outbound) => {
  if (inbound >= 2 * outbound) {
    return {
      key: 'cukup',
      value: true,
      color: '#0085FF',
      text_color: 'white',
      label: 'CUKUP',
    };
  }
  if (inbound < outbound) {
    return {
      key: 'kurang',
      value: false,
      color: '#ED3030',
      text_color: 'black',
      label: 'KURANG',
    };
  }
  return {
    key: 'sedang',
    value: false,
    color: '#FFE500',
    text_color: 'black',
    label: 'Sedang',
  };
};

// Payload not displayed as chart
export const IGNORED_PAYLOADS = [
  'datetime', 'counter',
];
